import React, { memo, useCallback, useRef, useState } from "react";
import clsx from "clsx";
import groupBy from "lodash/groupBy";
import styles from "./Grades.module.scss";
import Container from "../../../../../../../../shared-components/css-modules/lexus/atoms/Container";
import Text from "../../../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Grade from "./Grade";
import Button from "../../../../../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import slider from "../../../../../../../../shared-components/css-modules/lexus/utils/DraggableSlider.module.scss";
import a11y from "../../../../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { useSlider } from "../../../../../../../../../../common-deprecated/features/apheleia/hooks/useSlider";
import SubSectionWrapper from "../SubSectionWrapper";
import { CarConfigConfigureSectionsLbx } from "../../../../../../../utils/types/SectionTypesLbx";
import { useCarConfigSelector } from "../../../../../../../redux/store";
import type { SectionWrapperPropagationProps } from "../../../../../common/section-wrapper/SectionWrapper";
import SidePanel from "../../../../../../../../shared-components/css-modules/lexus/organisms/SidePanel";
import Compare from "../../../../compare/Compare";
import {
    trackBrowseAtmosphereGrades,
    trackClosingSidepanel,
    trackGradeCompare,
} from "../../../../../../../utils/tracking";
import { useAphLbxCheapestAtmosphere } from "../../../../../../../hooks/lbx/useAphLbxAtmospherePrice";
import Price from "../../../../../common/price/Price";
import { isRTL } from "../../../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { useAphLbxAtmospheres } from "../../../../../../../hooks/lbx/useAphLbxAtmospheres";
import { CarConfigLabel } from "../../../../../../../utils/labelUtils";

const Grades: React.FC<SectionWrapperPropagationProps> = ({ sectionRefs }): JSX.Element => {
    const atmospheres = useAphLbxAtmospheres();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const anyItemRef = useRef<HTMLDivElement>(null);
    const [compareGradeId, setCompareGradeId] = useState<string | undefined>();
    const items = groupBy(atmospheres, "category");
    const cheapestGrade = useAphLbxCheapestAtmosphere();

    const trackBrowse = useCallback(() => {
        trackBrowseAtmosphereGrades();
    }, []);

    const { navigate, prevButtonDisabled, nextButtonDisabled, elementProps, isDragging } = useSlider(
        wrapperRef,
        anyItemRef,
        trackBrowse,
        trackBrowse,
    );

    const gradeList = Object.entries(items);

    const isRtl = isRTL(commonSettings);

    const closeSidepanel = useCallback(() => {
        trackClosingSidepanel();
        setCompareGradeId(undefined);
    }, []);

    const onCompareGrade = useCallback((gradeId: string, gradeName: string) => {
        trackGradeCompare(gradeName);
        setCompareGradeId(gradeId);
    }, []);

    return (
        <SubSectionWrapper
            sectionId={CarConfigConfigureSectionsLbx.AtmosphereGrade}
            sectionRef={sectionRefs.get(CarConfigConfigureSectionsLbx.AtmosphereGrade)}
        >
            <Container>
                <div className={styles.intro}>
                    <Text tag="h2" variant="apheleia-h3">
                        <CarConfigLabel label="carConfigLbxChooseAtmosphere" />
                    </Text>
                    <Text tag="p" variant="apheleia-body">
                        <CarConfigLabel label="carConfigLbxChooseAtmosphereIntro" />
                    </Text>

                    <Text tag="p" variant="apheleia-body-small" className={styles.startprice}>
                        {cheapestGrade && (
                            <>
                                <CarConfigLabel label="carConfigLbxStartingPrice" />{" "}
                                <Price price={cheapestGrade.price} />
                            </>
                        )}
                    </Text>
                </div>
                <div className={styles["slider-controls"]}>
                    <Button
                        variant="btn-arrow"
                        circle
                        onClick={navigate(isRtl ? 1 : -1)}
                        disabled={isRtl ? nextButtonDisabled : prevButtonDisabled}
                    >
                        <Icon variant="chevron-left" sizing="md" aria-hidden="true" />
                        <span className={a11y["sr-only"]}>
                            <CarConfigLabel label="carConfigA11yButtonSlidePrevious" />
                        </span>
                    </Button>
                    <Button
                        variant="btn-arrow"
                        circle
                        onClick={navigate(isRtl ? -1 : 1)}
                        disabled={isRtl ? prevButtonDisabled : nextButtonDisabled}
                    >
                        <Icon variant="chevron-right" sizing="md" aria-hidden="true" />
                        <span className={a11y["sr-only"]}>
                            <CarConfigLabel label="carConfigA11yButtonSlideNext" />
                        </span>
                    </Button>
                </div>
            </Container>
            <div
                className={clsx(slider.wrapper, styles.slider, isDragging && slider.dragging)}
                ref={wrapperRef}
                {...elementProps}
            >
                {gradeList.map(([categoryName, grades]) => (
                    <div className={styles.group} key={categoryName}>
                        {categoryName && categoryName !== "undefined" && (
                            <Text tag="div" variant="apheleia-body-small" className={styles.legend}>
                                {categoryName}
                            </Text>
                        )}
                        {grades.map((grade) => (
                            <Grade ref={anyItemRef} key={grade.id} grade={grade} onCompareGrade={onCompareGrade} />
                        ))}
                    </div>
                ))}
            </div>
            <SidePanel
                variant="lbx-compare"
                open={typeof compareGradeId !== "undefined"}
                onRequestClose={closeSidepanel}
            >
                <Compare initialGradeId={compareGradeId} />
            </SidePanel>
        </SubSectionWrapper>
    );
};

export default memo(Grades);
